export interface GlyphVariant {
  src: string;
  width: number;
  height: number;
  baseline: number;
  fontSize?: number;
  lines?: GlyphLine[];
}

export interface GlyphLine {
  top: number;
  bottom: number;
  baseline: number;
}

export interface GlyphManifest {
  version: 1;
  source: string;
  entries: Record<string, GlyphVariant[]>;
}

export interface PaperVariant {
  src: string;
  width: number;
  height: number;
  ruling?: {
    firstLine: number;
    spacing: number;
    rotation: number;
  };
}

export interface PaperManifest {
  version: 1;
  source: string;
  variants: PaperVariant[];
}

function hash(value: string): number {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 0x01000193);
  }
  return result >>> 0;
}

function variation(seed: number, key: string, instance: number, salt: string): number {
  return hash(`${seed}/${key}/${instance}/${salt}`) / 4_294_967_296;
}

function safeCssUrl(url: string): string {
  return url.replaceAll("'", "%27").replaceAll("\\", "%5C");
}

export async function loadAssetManifests(): Promise<{
  glyphs: GlyphManifest;
  papers: PaperManifest;
}> {
  const [glyphResponse, paperResponse] = await Promise.all([
    fetch(versionedAssetUrl("/assets/handwriting/manifest.json")),
    fetch(versionedAssetUrl("/assets/paper/manifest.json")),
  ]);

  if (!glyphResponse.ok || !paperResponse.ok) {
    throw new Error("The notebook artwork could not be loaded.");
  }

  return versionManifests(
    (await glyphResponse.json()) as GlyphManifest,
    (await paperResponse.json()) as PaperManifest,
  );
}

// The build stamps a content hash of public/assets into the bundle (see vite.config.ts). Artwork
// URLs carry it as a query string so the files can be cached for a long time yet refresh as soon
// as they change. Outside the bundle, such as in unit tests, the identifier is undefined.
declare const __ASSET_VERSION__: string | undefined;

export function assetVersion(): string {
  return typeof __ASSET_VERSION__ === "string" ? __ASSET_VERSION__ : "dev";
}

export function versionedAssetUrl(src: string): string {
  return src.includes("?") ? src : `${src}?v=${assetVersion()}`;
}

export function versionManifests(glyphs: GlyphManifest, papers: PaperManifest): { glyphs: GlyphManifest; papers: PaperManifest } {
  return {
    glyphs: {
      ...glyphs,
      entries: Object.fromEntries(Object.entries(glyphs.entries).map(([key, variants]) => [
        key,
        variants.map((variant) => ({ ...variant, src: versionedAssetUrl(variant.src) })),
      ])),
    },
    papers: {
      ...papers,
      variants: papers.variants.map((variant) => ({ ...variant, src: versionedAssetUrl(variant.src) })),
    },
  };
}

export function glyphMarkup(
  manifest: GlyphManifest,
  key: string,
  seed: number,
  instance = 0,
  className = "",
): string {
  const variants = manifest.entries[key];
  if (!variants?.length) return "";
  const selected = variants[Math.floor(variation(seed, key, instance, "variant") * variants.length)];
  if (!selected) return "";

  const rotation = (variation(seed, key, instance, "rotation") - 0.5) * 5.4;
  const scale = 0.96 + variation(seed, key, instance, "scale") * 0.08;
  const drift = (variation(seed, key, instance, "drift") - 0.5) * 8;
  const opacity = 0.88 + variation(seed, key, instance, "opacity") * 0.1;
  const classes = ["glyph", className].filter(Boolean).join(" ");

  return `<span class="${classes}" aria-hidden="true" style="--glyph-image:url('${safeCssUrl(selected.src)}');--glyph-aspect:${selected.width}/${selected.height};--glyph-rotate:${rotation.toFixed(2)}deg;--glyph-scale:${scale.toFixed(3)};--glyph-drift:${drift.toFixed(2)}%;--glyph-opacity:${opacity.toFixed(3)}"></span>`;
}

export function selectGlyph(
  manifest: GlyphManifest,
  key: string,
  seed: number,
  instance = 0,
): GlyphVariant | undefined {
  const variants = manifest.entries[key];
  if (!variants?.length) return undefined;
  return variants[Math.floor(variation(seed, key, instance, "variant") * variants.length)];
}

export function paperForSeed(manifest: PaperManifest, seed: number): PaperVariant {
  const selected = manifest.variants[seed % manifest.variants.length];
  if (!selected) throw new Error("No paper variants are available.");
  return selected;
}